import { useAuth } from '@/context/use-auth';
import { useCoinBalance } from '@/context/use-coin-balance';
import { Link, useLocation } from 'react-router-dom';
import { useState, useRef } from 'react';

interface HeaderProps {
  showTOC?: boolean;
  onOpenTOC?: () => void;
}

export function Header({ showTOC, onOpenTOC }: HeaderProps) {
  const { user, signIn, logout, isAdmin } = useAuth();
  const { balance } = useCoinBalance();
  const location = useLocation();
  const isReader = location.pathname.startsWith('/book/');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-[var(--bg-primary)]/95 backdrop-blur-xl border-b border-[var(--border)]">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
          {/* Left - Back button (reader only) or spacer */}
          <div className="w-20 flex items-center">
            {isReader ? (
              <Link 
                to="/"
                className="flex items-center gap-1 text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"
              >
                <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="font-ui text-xs">Back</span>
              </Link>
            ) : null}
          </div>
          
          {/* Center - Logo and Wordmark */}
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-7 h-7 relative">
              <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="headerGoldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{stopColor:'#e8c878',stopOpacity:1}} />
                    <stop offset="50%" style={{stopColor:'#d4a853',stopOpacity:1}} />
                    <stop offset="100%" style={{stopColor:'#b8943f',stopOpacity:1}} />
                  </linearGradient>
                </defs>
                
                <path d="M 20 35 Q 35 25 50 35 Q 65 25 80 35 L 80 70 Q 65 60 50 70 Q 35 60 20 70 Z" 
                      fill="none" 
                      stroke="url(#headerGoldGradient)" 
                      strokeWidth="3"
                      strokeLinejoin="round" />
                
                <path d="M 50 35 L 50 70" 
                      stroke="url(#headerGoldGradient)" 
                      strokeWidth="2"
                      opacity="0.6" />
                
                <path d="M 25 40 Q 37 33 47 40" 
                      stroke="url(#headerGoldGradient)" 
                      strokeWidth="1.5" 
                      fill="none"
                      opacity="0.5" />
                <path d="M 53 40 Q 63 33 75 40" 
                      stroke="url(#headerGoldGradient)" 
                      strokeWidth="1.5" 
                      fill="none"
                      opacity="0.5" />
              </svg>
            </div>
            
            <span className="font-body text-lg font-semibold text-[var(--text-primary)] tracking-tight group-hover:text-[var(--accent)] transition-colors">
              Lynder
            </span>
          </Link>
          
          {/* Right - TOC (reader only) + Auth */}
          <div className="w-20 flex items-center justify-end gap-2">
            {isReader && showTOC && onOpenTOC && (
              <button 
                onClick={onOpenTOC}
                className="p-2 rounded-full text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            )}
            
            {user ? (
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 px-2 py-1 rounded-full bg-[var(--accent)]/10 border border-[var(--accent)]/20 hover:bg-[var(--accent)]/20 transition-colors"
                >
                  <svg className="w-3 h-3 text-[var(--accent)]" fill="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2"/>
                    <text x="12" y="16" textAnchor="middle" fontSize="8" fontWeight="bold">L</text>
                  </svg>
                  <span className="font-ui text-xs font-semibold text-[var(--accent)]">
                    {balance ?? user.tokenBalance}
                  </span>
                  <svg className="w-3 h-3 text-[var(--accent)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {showUserMenu && (
                  <div className="absolute right-0 top-full mt-2 w-48 bg-[var(--bg-secondary)] border border-[var(--border)] rounded-xl shadow-lg py-2 z-50">
                    <div className="px-4 py-2 border-b border-[var(--border)]">
                      <p className="font-ui text-xs text-[var(--text-tertiary)]">Signed in as</p>
                      <p className="font-ui text-sm text-[var(--text-primary)] truncate">{user.email}</p>
                    </div>
                    
                    <Link
                      to="/billing"
                      onClick={() => setShowUserMenu(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="font-ui text-sm">Get Coins</span>
                    </Link>
                    
                    {isAdmin && (
                      <Link
                        to="/admin"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="font-ui text-sm">Admin</span>
                      </Link>
                    )}
                    
                    <div className="border-t border-[var(--border)] mt-2 pt-2">
                      <button
                        onClick={() => {
                          logout();
                          setShowUserMenu(false);
                        }}
                        className="flex items-center gap-3 px-4 py-2.5 text-red-400 hover:text-red-300 hover:bg-red-400/10 transition-colors w-full"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        <span className="font-ui text-sm">Sign out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={signIn}
                className="font-ui text-xs font-medium text-[var(--bg-primary)] bg-[var(--accent)] hover:bg-[var(--accent)]/90 px-3 py-1.5 rounded-full transition-all active:scale-95"
              >
                Sign in
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="h-14" />
    </>
  );
}
